import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Trash2, Sparkles, Search, X } from "lucide-react";
import { GARDENS } from "@/lib/gardens";

const EntryList = ({ type }) => {
    const [entries, setEntries] = useState([]);
    const [form, setForm] = useState({ title: "", content: "", garden_tags: [] });
    const [busy, setBusy] = useState(false);
    const [q, setQ] = useState("");
    const [filterGarden, setFilterGarden] = useState("");

    const load = async () => {
        const params = new URLSearchParams({ entry_type: type });
        if (q.trim()) params.set("q", q.trim());
        if (filterGarden) params.set("garden", filterGarden);
        const { data } = await api.get(`/journal?${params.toString()}`);
        setEntries(data);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, filterGarden]);
    useEffect(() => {
        const id = setTimeout(load, 250);
        return () => clearTimeout(id);
        // eslint-disable-next-line
    }, [q]);

    const toggleTag = (k) => {
        setForm((f) => ({
            ...f,
            garden_tags: f.garden_tags.includes(k) ? f.garden_tags.filter((x) => x !== k) : [...f.garden_tags, k],
        }));
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.content.trim()) return;
        setBusy(true);
        try {
            await api.post("/journal", {
                entry_type: type,
                title: form.title || null,
                content: form.content,
                garden_tags: form.garden_tags,
            });
            setForm({ title: "", content: "", garden_tags: [] });
            toast.success("Entry saved.");
            load();
        } finally { setBusy(false); }
    };

    const remove = async (id) => {
        await api.delete(`/journal/${id}`);
        load();
    };

    return (
        <div className="grid md:grid-cols-12 gap-12 mt-8">
            <form onSubmit={save} className="md:col-span-5 space-y-4" data-testid={`journal-form-${type}`}>
                <Input
                    placeholder="A title (optional)"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="h-11 rounded-md border-foreground/15 bg-transparent"
                />
                <Textarea
                    placeholder={type === "prayer" ? "Speak honestly to God…" : "Write what is quietly true today…"}
                    value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                    rows={8}
                    className="rounded-md border-foreground/15 bg-transparent resize-none"
                    data-testid={`journal-content-${type}`}
                />
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Connect to a garden (optional)</div>
                    <div className="flex flex-wrap gap-2">
                        {GARDENS.map((g) => {
                            const active = form.garden_tags.includes(g.key);
                            return (
                                <button
                                    type="button"
                                    key={g.key}
                                    onClick={() => toggleTag(g.key)}
                                    data-testid={`journal-tag-${g.key}`}
                                    className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                                        active
                                            ? "bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] border-[hsl(var(--eden-forest))]"
                                            : "border-foreground/15 hover:border-foreground/40"
                                    }`}
                                >
                                    {g.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <Button
                    type="submit"
                    disabled={busy || !form.content.trim()}
                    data-testid={`journal-save-${type}`}
                    className="bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-forest))]/90 rounded-md"
                >
                    Save entry
                </Button>
            </form>
            <div className="md:col-span-7 space-y-6">
                {/* Search + filter */}
                <div className="space-y-3">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search reflections…"
                            className="pl-10 h-11 rounded-md border-foreground/15 bg-transparent"
                            data-testid={`journal-search-${type}`}
                        />
                        {q && (
                            <button
                                type="button"
                                onClick={() => setQ("")}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                aria-label="Clear search"
                            >
                                <X className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        <button
                            type="button"
                            onClick={() => setFilterGarden("")}
                            data-testid={`journal-filter-all-${type}`}
                            className={`text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-md border transition-colors ${
                                filterGarden === ""
                                    ? "bg-foreground text-background border-foreground"
                                    : "border-foreground/15 text-muted-foreground hover:border-foreground/40"
                            }`}
                        >
                            All
                        </button>
                        {GARDENS.map((g) => {
                            const active = filterGarden === g.key;
                            return (
                                <button
                                    type="button"
                                    key={g.key}
                                    onClick={() => setFilterGarden(active ? "" : g.key)}
                                    data-testid={`journal-filter-${g.key}-${type}`}
                                    className={`text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-md border transition-colors ${
                                        active
                                            ? "bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] border-[hsl(var(--eden-forest))]"
                                            : "border-foreground/15 text-muted-foreground hover:border-foreground/40"
                                    }`}
                                >
                                    {g.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {entries.length === 0 && (
                    <div className="text-muted-foreground text-sm" data-testid={`journal-empty-${type}`}>
                        {q || filterGarden ? "No entries match your search." : "No entries yet. Begin gently."}
                    </div>
                )}
                {entries.map((e) => (
                    <div key={e.entry_id} className="hairline rounded-lg p-6 group" data-testid={`journal-entry-${e.entry_id}`}>
                        <div className="flex justify-between items-baseline mb-2">
                            <div>
                                {e.title && <div className="font-serif text-2xl mb-1">{e.title}</div>}
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                    {new Date(e.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                                </div>
                            </div>
                            <button
                                onClick={() => remove(e.entry_id)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                                aria-label="Delete"
                            >
                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                        </div>
                        <p className="text-sm leading-relaxed whitespace-pre-wrap mt-3">{e.content}</p>
                        {e.garden_tags?.length > 0 && (
                            <div className="mt-4 flex flex-wrap gap-1.5">
                                {e.garden_tags.map((t) => {
                                    const lbl = GARDENS.find((g) => g.key === t)?.label || t;
                                    return (
                                        <span key={t} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-md bg-foreground/[0.06] text-muted-foreground">
                                            {lbl}
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const InsightsPanel = () => {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/insights/patterns");
            setData(data);
        } finally { setBusy(false); }
    };
    useEffect(() => { load(); }, []);

    if (busy && !data) return <div className="text-sm text-muted-foreground py-8">Looking for patterns…</div>;
    if (!data) return null;

    return (
        <div className="mt-4" data-testid="insights-panel">
            <div className="hairline rounded-lg p-8 md:p-10 relative overflow-hidden">
                <div className="absolute -right-8 -top-8 opacity-[0.06] pointer-events-none">
                    <Sparkles className="w-40 h-40 text-[hsl(var(--eden-gold))]" strokeWidth={0.5} />
                </div>
                <div className="text-[10px] uppercase tracking-[0.4em] text-[hsl(var(--eden-gold))] mb-4 flex items-center gap-2">
                    <Sparkles className="w-3 h-3" strokeWidth={1.5} /> Patterns · last 90 days
                </div>
                {data.summary ? (
                    <p className="font-serif text-2xl md:text-3xl leading-snug max-w-3xl" data-testid="insights-summary">
                        {data.summary}
                    </p>
                ) : (
                    <p className="text-muted-foreground">EDEN will begin noticing patterns as you write. Try tagging your next entry with a garden.</p>
                )}
                {data.patterns?.length > 0 && (
                    <ul className="mt-8 space-y-3 max-w-3xl">
                        {data.patterns.map((p, i) => (
                            <li key={i} className="flex gap-4 items-baseline">
                                <span className="text-[hsl(var(--eden-gold))]">·</span>
                                <span className="text-base leading-relaxed">{p}</span>
                            </li>
                        ))}
                    </ul>
                )}
                <button onClick={load} className="mt-6 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground" data-testid="insights-refresh">
                    Refresh insights
                </button>
            </div>
        </div>
    );
};

export default function Journal() {
    return (
        <div className="space-y-8">
            <header className="max-w-3xl">
                <div className="text-[10px] uppercase tracking-[0.4em] text-[hsl(var(--eden-gold))] mb-4">Stewardship journal</div>
                <h1 className="font-serif text-5xl md:text-7xl leading-[1.02]">Write it honestly.</h1>
                <p className="mt-6 text-muted-foreground text-lg leading-relaxed">
                    A place for reflection and prayer, connected to the gardens they belong to.
                    Over time, EDEN quietly names the patterns you may not see.
                </p>
            </header>

            <InsightsPanel />

            <Tabs defaultValue="reflection" className="w-full">
                <TabsList className="bg-transparent border-b border-foreground/10 rounded-none w-full justify-start h-auto p-0 gap-8">
                    <TabsTrigger
                        value="reflection"
                        data-testid="tab-reflection"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-[hsl(var(--eden-gold))] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 text-[10px] uppercase tracking-[0.35em]"
                    >
                        Reflection
                    </TabsTrigger>
                    <TabsTrigger
                        value="prayer"
                        data-testid="tab-prayer"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-[hsl(var(--eden-gold))] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 text-[10px] uppercase tracking-[0.35em]"
                    >
                        Prayer Journal
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="reflection"><EntryList type="reflection" /></TabsContent>
                <TabsContent value="prayer"><EntryList type="prayer" /></TabsContent>
            </Tabs>
        </div>
    );
}
